import { ZZFX_SAMPLE_RATE as SR } from '../lib/zzfx';
import { bp, env, lp, noise } from './punch';

/**
 * Chomper, the combine (ADR-0180, Cars' Frank): its sounds are tones, not noise, synthesised past ZzFX (whose noise is a
 * pitched wave, and whose single oscillator through a low-pass only hisses this low).
 */

/**
 * The harmonics of a voice whose fundamental is `f0` (Hz, a sample each), harmonic `n` at `gain(n, n × f0, t)` (taken
 * every 64 samples), up to `top` Hz: summed by the Chebyshev recurrence, one sine and cosine a sample however many.
 */
function voice(f0: Float32Array, gain: (n: number, f: number, t: number) => number, top: number): Float32Array {
  let low = Number.POSITIVE_INFINITY;
  for (const f of f0) low = Math.min(low, f);
  const most = Math.max(1, Math.floor(top / low));
  const w = new Float32Array(most + 1);
  const out = new Float32Array(f0.length);
  let phase = 0;
  for (let i = 0; i < f0.length; i++) {
    const f = f0[i] as number;
    if (i % 64 === 0) for (let n = 1; n <= most; n++) w[n] = n * f < top ? gain(n, n * f, i / SR) : 0;
    phase = (phase + (2 * Math.PI * f) / SR) % (2 * Math.PI);
    const c = Math.cos(phase);
    let prev = 0;
    let cur = Math.sin(phase);
    let sum = (w[1] as number) * cur;
    for (let n = 2; n <= most; n++) {
      const next = 2 * c * cur - prev;
      prev = cur;
      cur = next;
      sum += (w[n] as number) * next;
    }
    out[i] = sum;
  }
  return out;
}

/** `x` driven through tanh by `drive`, levelled to `peak`, the silent tail cut with a 10 ms fade. */
function finish(x: Float32Array, drive: number, peak: number): Float32Array {
  let top = 0;
  const y = x.map((v) => Math.tanh(drive * v));
  for (const v of y) top = Math.max(top, Math.abs(v));
  let end = y.length;
  while (end > 1 && Math.abs(y[end - 1] as number) < 0.002 * top) end--;
  const out = y.slice(0, end).map((v) => (v / (top || 1)) * peak);
  const fade = Math.min(Math.round(0.01 * SR), out.length);
  for (let i = 0; i < fade; i++) out[out.length - 1 - i] = (out[out.length - 1 - i] as number) * (i / fade);
  return out;
}

const smooth = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

/**
 * Its horn, the bellow (Frank's): a low brass power chord (A1, its fifth and octave, a sub an octave under) that scoops
 * up into pitch, sways with a slow vibrato and sags as it dies; its mouth opening from "oo" to "aah" (a formant sliding
 * up the harmonics), a growl through it (an amplitude rattle at 27 Hz), warmed through tanh. 1.9 s.
 */
export function hornSamples(): Float32Array {
  const T = 1.9;
  const n = Math.round(T * SR);
  const pitch = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const t = i / SR;
    const scoop = 0.8 + 0.2 * (1 - Math.exp(-t / 0.06));
    const vibrato = 1 + 0.012 * Math.sin(2 * Math.PI * 4.6 * t) * smooth(t / 0.4);
    const sag = 1 - 0.16 * smooth((t - (T - 0.55)) / 0.55);
    pitch[i] = scoop * vibrato * sag;
  }
  // Brass: harmonics falling as 1/n^0.85, lifted round two formants that open over its first quarter second.
  const brass = (k: number, f: number, t: number) => {
    const open = smooth(t / 0.25);
    const f1 = 320 + 380 * open;
    const f2 = 1100 + 200 * open;
    const formant = 0.25 + 1 / (1 + ((f - f1) / 160) ** 2) + 0.45 / (1 + ((f - f2) / 260) ** 2);
    return formant / k ** 0.85;
  };
  const at = (ratio: number) => pitch.map((p) => p * ratio);
  const root = voice(at(55), brass, 2600);
  const fifth = voice(at(82.41 * 1.003), brass, 2600);
  const octave = voice(at(110 * 0.998), brass, 2600);
  const sub = voice(at(27.5), (k) => (k === 1 ? 1 : 0), 60);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const t = i / SR;
    const amp = smooth(t / 0.07) * (t > T - 0.5 ? Math.exp(-(t - (T - 0.5)) / 0.16) : 1);
    const growl = 1 + 0.22 * Math.sin(2 * Math.PI * 27 * t);
    const v = (root[i] as number) + 0.6 * (fifth[i] as number) + 0.35 * (octave[i] as number);
    out[i] = (v * 0.12 * growl + 0.3 * (sub[i] as number)) * amp;
  }
  return finish(lp(out, 3200), 1.4, 0.85);
}

/**
 * One firing of its diesel (played every 0.18 s as it drives, a chug): a low thump dropping from 78 to 48 Hz with its
 * second and third harmonics fading faster, a breath of exhaust under it, punched through tanh. 0.16 s.
 */
export function dieselSamples(): Float32Array {
  const n = Math.round(0.16 * SR);
  const out = new Float32Array(n);
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / SR;
    phase += (2 * Math.PI * (48 + 30 * Math.exp(-t / 0.02))) / SR;
    const v =
      Math.sin(phase) +
      0.5 * Math.exp(-t / 0.03) * Math.sin(2 * phase) +
      0.25 * Math.exp(-t / 0.02) * Math.sin(3 * phase);
    out[i] = v * (t < 0.003 ? t / 0.003 : Math.exp(-(t - 0.003) / 0.04));
  }
  const puff = env(bp(noise(0.16, 31), 250, 1.2), 0.002, 0.02);
  for (let i = 0; i < n; i++) out[i] = 0.8 * (out[i] as number) + 0.12 * (puff[i] ?? 0);
  return finish(out, 2.2, 0.6);
}

/**
 * A bat of its reel coming down (every 0.4 s, its art's turn): a heavy whoomp, a low sine dropping from 100 to 40 Hz
 * with its octave, and a soft swish of air before it. 0.32 s.
 */
export function reelSamples(): Float32Array {
  const n = Math.round(0.32 * SR);
  const out = new Float32Array(n);
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / SR;
    phase += (2 * Math.PI * (40 + 60 * Math.exp(-t / 0.05))) / SR;
    const amp = t < 0.012 ? t / 0.012 : Math.exp(-(t - 0.012) / 0.08);
    out[i] = (Math.sin(phase) + 0.3 * Math.sin(2 * phase)) * amp;
  }
  const swish = env(bp(noise(0.32, 37), 500, 0.8), 0.05, 0.06);
  for (let i = 0; i < n; i++) out[i] = (out[i] as number) + 0.05 * (swish[i] ?? 0);
  return finish(out, 1.6, 0.5);
}
